import { useEffect, useRef } from 'react';

import type { RecentViewType } from 'api/user-settings/types';

import { useUpdateUserSettings } from './useUpdateUserSettings';

export interface TrackRecentViewOptions {
  projectId: string | undefined;
  viewType: RecentViewType;
  /**
   * The query id for a Query view, or the template node id for a Hierarchy or NodeList view. Taken from the view's
   * own route params, so that live and proposal visits, and any nested details route, all map to the same entry.
   */
  targetId: string | undefined;
  /** The project view and header from the route, when the view was opened under a perspective. */
  viewId?: string;
  headerId?: string;
  /** The view's display name. Leave undefined until it has resolved, so a placeholder name is never stored. */
  name: string | undefined;
  templateNodeId?: string;
}

/** Records the current view as recently opened, once per view the user lands on. */
export const useTrackRecentView = ({
  projectId,
  viewType,
  targetId,
  viewId,
  headerId,
  name,
  templateNodeId
}: TrackRecentViewOptions) => {
  const { mutate: updateUserSettings } = useUpdateUserSettings({ silent: true });
  const recordedViewRef = useRef<string>();

  useEffect(() => {
    if (!projectId || !targetId || !name) {
      return;
    }

    const perspective = viewId && headerId ? { viewId, headerId } : undefined;
    const viewKey = [projectId, viewType, targetId, perspective?.viewId, perspective?.headerId].join('/');

    if (recordedViewRef.current === viewKey) {
      return;
    }

    recordedViewRef.current = viewKey;

    const view = { viewType, targetId, perspective, name, templateNodeId };

    // PersonalSettings merges the view into the project's stored list, so only the new visit needs sending.
    updateUserSettings({ projectsSettings: { [projectId]: { recentViews: [view] } } });
  }, [projectId, viewType, targetId, viewId, headerId, name, templateNodeId, updateUserSettings]);
};
